import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import { createEnvironment, updateEnvironment } from '../../api/documentAPI';
import { generateChromaName } from '../../services/generateEnvironmentName';
import { getEnvironmentsforUser, deleteEnvironment, getEnvironmentById, getTeams } from '../../api/documentAPI';
import { useAtom } from 'jotai';
import { allowDeleteAtom } from '../../atoms/managedocs/useAllowDeleteAtom';
import { useManageDocsEnvironmentAtom } from '../../atoms/managedocs/useManageDocsEnvironmentAtom';
import { useSelectedEnvTypeAtom } from '../../atoms/managedocs/useSelectedEnvTypeAtom';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';

import useCookie from "../../hooks/useCookie";
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import TeamsMultiselect from '../shared/TeamsMultiselect';
import EnvTypeSelect from '../shared/EnvTypeSelect';
import ProcessingModeFields from './ProcessingModeFields';
import FolderDescriptionField from './FolderDescriptionField';

const EnvironmentsList = ({ onEnvItemClick }) => {

    const { showNotification } = useNotification();

    const [t, ] = useTranslation('global');
    const [ , setCookie, ] = useCookie("ENV");
    
    const [allowDelete, setAllowDelete] = useAtom(allowDeleteAtom);
    const [docsEnvironmentId, setDocsEnvironmentId] = useManageDocsEnvironmentAtom();
    const [, setSelectedEnvType] = useSelectedEnvTypeAtom();

    const [environments, setEnvironments] = useState([]);
    const [, setTeams] = useState([]);

    const [openAddEnv, setOpenAddEnv] = useState(false);
    const [openEditEnv, setOpenEditEnv] = useState(false);
    const [openConfirmDel, setOpenConfirmDel] = useState(false);   
    const [bouncing, setBouncing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [environmentIdToDelete, setEnvironmentIdToDelete] = useState(null);
    const [envToDelete, setEnvToDelete] = useState('');
    const [clickedEnvId, setclickedEnvId] = useState(null);
    const [activeDiv, setActiveDiv] = useState(null);

    // predvolene offline spracovanie (docling) s OCR skenov
    const initialFormData = {name: '', folderName: '', roleId: '', teamIds: [], type: 'M', docIntelligence: false, imgProcessing: true,
                             description: '', descriptionLocked: false};
    const [formData, setFormData] = useState(initialFormData);
    const [updateForm, setUpdateForm] = useState({ id: '', name: '', teamIds : [], docIntelligence: false, imgProcessing: false,
                                                     description: '', descriptionLocked: false, descriptionUpdatedAt: null});

    const fetchEnvironments = () => {
        getEnvironmentsforUser()
        .then((data) => {
            setAllowDelete(false);
            setEnvironments(data);    

            const containsId = data.some(item => item.Id === docsEnvironmentId);
            if (data.length > 0 && (docsEnvironmentId == undefined || containsId == false)) {
                setDocsEnvironmentId(data[0].Id);
                setSelectedEnvType(data[0].Type);
            } 
        })
    }

    const fetchEnvironment = (id) => {
        getEnvironmentById(id)
        .then(data => {
            setUpdateForm({
                name: data.DocsEnv.Name,
                teamIds: data.DocsEnv.TeamIds,
                docIntelligence: !!data.DocsEnv.DocIntelligence,
                imgProcessing: !!data.DocsEnv.ImgProcessing,
                description: data.DocsEnv.Description || '',
                descriptionLocked: !!data.DocsEnv.DescriptionLocked,
                descriptionUpdatedAt: data.DocsEnv.DescriptionUpdatedAt || null
            })
        });
    }

    const fetchTeams = async () => {
        const response = await getTeams();
        const teams = response.map((team) => ({
          value : team.Id,
          label: team.Name
        }));
        setTeams(teams);
  }

    useEffect(() => {
        fetchTeams();
        fetchEnvironments();
    }, []);

    // Add Environment Modal
    const handleOpen_AddModal = () => setOpenAddEnv(true);   
    const handleClose_AddModal = () => {
        setOpenAddEnv(false);
        setActiveDiv(null);
        setFormData(initialFormData);
    };
    const handleTeamChange_AddModal= (event) => {
        const {
            target: { value },
        } = event;
        setFormData({
            ...formData,
            teamIds: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const handleSubmit_AddModal = (e) => {
        e.preventDefault();
        setIsLoading(true);
    
        if (!formData.name || formData.name.trim() === '' || formData.name.length < 3 || formData.name.length > 63) {
            showNotification({ message: t('envNameRuleMsg'), type: NotificationType.Error });
            setIsLoading(false);
            return;
        }
    
        // if (!formData.teamIds.length > 0) {
        //     showNotification({ message: t('selectTeamsRequired'), type: NotificationType.Error });
        //     setIsLoading(false);
        //     return;
        // }
    
        let chromaName = generateChromaName(JSON.stringify(formData.name));
        let payload = {
            "Name": formData.name,
            "FolderName": chromaName,
            "RoleId": 1,
            "TeamIds": formData.teamIds,
            "Type": formData.type,
            "DocIntelligence": formData.docIntelligence,
            "ImgProcessing": formData.imgProcessing,
            "Description": formData.description,
            "DescriptionLocked": formData.descriptionLocked
        };


    
        createEnvironment(payload)
            .then(res => {
                // folder name already exists
                if (res.Status === 'ERROR') {
                    showNotification({ message: t('envExists'), type: NotificationType.Error });
                    setIsLoading(false);
                } else if (res.Data) {
                    getEnvironmentsforUser()
                        .then((userEnvs) => {

                            // If the user creating the env belongs to him, set it as default, else leave it as is 
                            const envExists = userEnvs.some(env => env.Id === res.Data.Id);
                            if (envExists) {
                                const createdEnv = userEnvs.find(env => env.Id === res.Data.Id);
                                setDocsEnvironmentId(res.Data.Id);
                                if (createdEnv?.Type) {
                                    setSelectedEnvType(createdEnv.Type);
                                }
                            }
    
                            // správca nie je v žiadnom z tímov priečinka -> v zozname ho nemá (vidia ho len tímy)
                            showNotification(envExists
                                ? { message: t('folderCreated'), type: NotificationType.Success }
                                : { message: t('folderCreatedHidden'), type: NotificationType.Info });

                            setAllowDelete(false);
                            setEnvironments(userEnvs);
                            setIsLoading(false);
                            setFormData(initialFormData);
                        })
                        .catch(error => {
                            console.error("Error fetching environments for user:", error);
                            setIsLoading(false);
                        });
                } else {
                    showNotification({ message: t('errorOccured'), type: NotificationType.Error });
                    setIsLoading(false);
                }
            })
    
        setTimeout(() => {
            handleClose_AddModal();
        }, 1000);
    };
    
    // Edit Environment Modal
    const handleOpen_EditModal = (id) => {
        fetchEnvironment(id);
        setOpenEditEnv(true);
    };
    const handleClose_EditModal = () => {
        setOpenEditEnv(false);
        setBouncing(false);
        setActiveDiv(null);
    };
    const handleTeamChange_EditModal = (event) => {
        const {
            target: { value },
        } = event;
        setUpdateForm({
            ...updateForm,
            teamIds: typeof value === 'string' ? value.split(',') : value,
        });
    }; 
    
    const handleSubmit_EditModal = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const updatedForm = { ...updateForm, id: clickedEnvId };
        if (!updatedForm.teamIds?.length) {
            showNotification({ 
                message: t('selectTeamsRequired'), 
                type: NotificationType.Error 
            });
            setIsLoading(false);
            return;
        }

        updateEnvironment(updatedForm)
        .then(() => {
            showNotification({ 
                message: t('savedChanges'), 
                type: NotificationType.Success 
            });
            return fetchEnvironments();
        })
        .catch(error => {
            showNotification({ 
                message: t('errorOccured'), 
                type: NotificationType.Error 
            });
        })
        .finally(() => {
            setIsLoading(false);
            setTimeout(handleClose_EditModal, 1000);
        });
    };

    const handleBounce = () => {
        setBouncing((prevBouncing) => !prevBouncing);
    };

    // Confirmation Modal
    const handleOpenConfirmDelModal = (id, name) => {
        setEnvironmentIdToDelete(id);
        setEnvToDelete(name);
        setOpenConfirmDel(true);
    };
    const handleCloseConfirmDelModal = () => {
        setEnvironmentIdToDelete(null);
        setOpenConfirmDel(false);
        setAllowDelete(false);
        setActiveDiv(null);
    }
    const handleNo = () => {
        handleCloseConfirmDelModal();
    }
    const handleYes = async () => {
        // okno ostane otvorené so „Mažem…“, kým backend priečinok nezmaže (index, súbory – môže trvať)
        setIsDeleting(true);
        try {
            await handleDeleteEnvironment(environmentIdToDelete);
        } finally {
            setIsDeleting(false);
            handleCloseConfirmDelModal();
        }
    }

    const handleEnvItemClick = (id, type) => {

        setDocsEnvironmentId(id);
        setSelectedEnvType(type);

        if (bouncing) {   // edit režim otvorí premenovanie pre každý typ (aj SharePoint 'S')
            setclickedEnvId(id);
            handleOpen_EditModal(id);
        } else {
            onEnvItemClick(id, type);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    } 

    const handleAllowDelete = () => {
        setAllowDelete((prevAllowDelete) => !prevAllowDelete);
    }

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm({ ...updateForm, [name]: value });
    }

    const handleDeleteEnvironment = async (id) => {

        try {
            setIsLoading(true);
            
            await deleteEnvironment(id);
            const data = await getEnvironmentsforUser();
            showNotification({  message: t('folderDeleted'), type: NotificationType.Success });
            
            setAllowDelete(false);
            setEnvironments(data);
            if (data && data.length > 0) {
                setDocsEnvironmentId(data[0].Id);
                setCookie("ENV", JSON.stringify(data[0].Id));
            } else {
                setDocsEnvironmentId(null);
                setCookie("ENV", null);
            }
    
        } catch (error) {
            console.error(error);
            showNotification({ message: t('errorOccured'),  type: NotificationType.Error });
        } finally {
            setIsLoading(false);
        }
            
    } 
    
    const handleDivClick = (id) => {
        setActiveDiv(prev => prev === id ? null : id); // toggle active state
    }

    return (
        <>
            <div id="env-manager-btns" className='env-manager'>
                <div
                    className={`btn-group ${activeDiv === 'add' ? 'active' : ''}`}
                    onClick={() => {
                        handleOpen_AddModal();
                        handleDivClick('add');
                    }}
                    tabIndex="0"
                >
                    <span className="material-symbols-outlined env-icon">add</span>
                </div>

                <div
                    className={`btn-group ${activeDiv === 'edit' ? 'active' : ''}`}
                    onClick={() => {
                        handleBounce();
                        handleDivClick('edit');
                    }}
                    tabIndex="0"
                >
                    <span className="material-symbols-outlined env-icon">edit</span>
                </div>

                <div
                    className={`btn-group ${activeDiv === 'delete' ? 'active' : ''}`}
                    onClick={() => {
                        handleAllowDelete();
                        handleDivClick('delete');
                    }}
                    tabIndex="0"
                > 
                    <span className="material-symbols-outlined env-icon">do_not_disturb_on</span>
                </div>
            </div>

            <Modal
                open={openAddEnv}
                onClose={handleClose_AddModal}
                aria-labelledby="env-modal-title"
                aria-describedby="env-modal-description"
            >
                <div className='env-modal'>
                    <div className='new-env-title'><h1>{t('newEnvironment')}</h1></div>
                    <form className='new-env-input-form'>

                        <div className='input-line'>
                            <label htmlFor='name-inp'>{t('newEnvName')}</label>
                            <input type="text" id="name-inp" name="name"
                                value={formData.name} onChange={handleChange}
                            />
                        </div>

                        <div className='input-line'>
                            <EnvTypeSelect value={formData.type} onChange={handleChange} name="type" />
                            <span className="env-type-hint">
                                {formData.type === 'S'
                                    ? (t('typeSharePointHint') || 'SharePoint: dokumenty sa automaticky synchronizujú zo SharePointu; zdrojový súbor sa po indexácii zmaže (zdroj ostáva v SharePointe).')
                                    : formData.type === 'N'
                                    ? (t('typeNetworkDriveHint') || 'Zdieľaný disk: dokumenty sa automaticky synchronizujú zo zdieľaného disku. Súbory ostávajú len na disku (aplikácia si ich po spracovaní neukladá); stiahnutie ide priamo z disku.')
                                    : (t('typeManualHint') || 'Manual: dokumenty nahrávate ručne; súbory zostávajú v aplikácii a dajú sa stiahnuť.')}
                            </span>
                        </div>

                        <div className='input-line'>
                                <TeamsMultiselect selectedValues={formData.teamIds} onChange={handleTeamChange_AddModal}/>

                        </div>

                        <div className='input-line'>
                            <ProcessingModeFields
                                docIntelligence={formData.docIntelligence}
                                imgProcessing={formData.imgProcessing}
                                onChange={(v) => setFormData({ ...formData, ...v })}
                            />
                        </div>

                        <div className='input-line'>
                            <FolderDescriptionField
                                description={formData.description}
                                locked={formData.descriptionLocked}
                                onChange={(v) => setFormData((f) => ({ ...f, ...v }))}
                            />
                        </div>
                        
                        <button type="button" id="new-env-close" className='return-button' disabled={isLoading} onClick={handleClose_AddModal}>
                            {t('cancel')}
                        </button>
                        <button type="submit" id="new-env-submit" className='submit-button' disabled={isLoading} onClick={handleSubmit_AddModal}>
                            {t('save')}
                        </button>
                    </form>
                </div>
            </Modal>

            <Modal 
                open={openEditEnv}
                onClose={handleClose_EditModal}
            > 
            <div className='env-modal'>
                <form>
                    <div className='edit-env-title'><h1>{t('edit')}</h1></div>
                        <div className="input-section">
                                <div className="input-line">
                                    <label htmlFor="name">{t('folderName')}</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={updateForm.name}
                                        onChange={handleUpdateChange}
                                    />
                                </div> 
                                <div className='input-line'>
                                    <div className='selectbox-section team-selectbox-wrapper'>  
                                        <TeamsMultiselect selectedValues={updateForm.teamIds} onChange={handleTeamChange_EditModal}/>
                                    </div>
                                </div>
                                <div className='input-line'>
                                    <ProcessingModeFields
                                        docIntelligence={updateForm.docIntelligence}
                                        imgProcessing={updateForm.imgProcessing}
                                        onChange={(v) => setUpdateForm({ ...updateForm, ...v })}
                                        isEdit
                                    />
                                </div>
                                <div className='input-line'>
                                    <FolderDescriptionField
                                        description={updateForm.description}
                                        locked={updateForm.descriptionLocked}
                                        updatedAt={updateForm.descriptionUpdatedAt}
                                        envId={clickedEnvId}
                                        onChange={(v) => setUpdateForm((f) => ({ ...f, ...v }))}
                                    />
                                </div>
                            </div>

                        <button type="button" id="upd-env-close" className='return-button' disabled={isLoading} onClick={handleClose_EditModal}>
                            {t('cancel')}
                        </button>
                        <button type="submit" id="upd-env-submit" className='submit-button' disabled={isLoading} onClick={handleSubmit_EditModal}>
                        {t('save')}
                        </button>              
                </form>   
            </div>
        </Modal> 

        <div className="tabs-wrapper">
            <div className="tabs">
            {environments.map((env) => (
                <React.Fragment key={env.Id}>
                    <div className={bouncing ? "one-tab wobble-animation" : "one-tab"}>
                        <input 
                            type='radio'
                            id={env.Id}
                            name="tabs"
                            checked={env.Id === docsEnvironmentId}
                            onChange={() => {}} // dummy handler to avoid dom exception 
                            onClick={()=> handleEnvItemClick(env.Id, env.Type)}
                        />
                        <label
                            className={`${bouncing ? "wobble-selectable" : "tab"} ${clickedEnvId === env.Id ? "clicked" : ""} ${env.Type === 'S' ? "type-sp" : env.Type === 'N' ? "type-nd" : ""}`}
                            htmlFor={`${env.Id}`}
                        >
                            {env.Name}
                            {allowDelete ? (
                                <span className="material-symbols-outlined" onClick={() => handleOpenConfirmDelModal(env.Id, env.Name)}>
                                    close
                                </span>
                            ) : env.Type === 'S' ? (
                                <span className="material-symbols-outlined">
                                    cloud
                                </span>
                            ) : env.Type === 'N' ? (
                                <span className="material-symbols-outlined">
                                    folder_shared
                                </span>
                            ) : null}

                        </label>                                                     
                    </div>
                </React.Fragment>
            ))}
            </div>
        </div>
        <ConfirmDeleteModal
            open={openConfirmDel}
            onNo={handleNo}
            onYes={handleYes}
            onClose={handleCloseConfirmDelModal}
            loading={isDeleting}
            item={envToDelete}
            note={(() => {
              const env = environments.find(e => e.Id === environmentIdToDelete);
              if (!env?.HasSyncMapping) return undefined;
              if (env.Type === 'S') return t('deleteSyncedEnvNote') || 'Priečinok je napojený na SharePoint synchronizáciu — odstráni sa aj toto mapovanie (pripojenie ostane).';
              if (env.Type === 'N') return t('deleteSyncedNdEnvNote') || 'Priečinok je napojený na synchronizáciu zo zdieľaného disku — odstráni sa aj toto mapovanie (disk ostane pripojený).';
              return undefined;
            })()}
        ></ConfirmDeleteModal>
        </>
    );
}

export default EnvironmentsList;